import { useEffect, useMemo, useState } from "react";
import { bool, func, object, string } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { RoundIntervals, BreakdownTableDepths } from "@repo/utils";
import { WORK_TYPE_SETTINGS } from "@tools/EstimateEditor/utils";
import { RoundingSettingsContents, useEstimateStructure } from "@tools";
import { Accordion, Alert, Button } from "@components";
import Grid from "@material-ui/core/Grid";
import { Cached } from "@material-ui/icons";

export const RoundingSettings = observer(({
  workType,
  expandedAccordion,
  onToggle,
  advancedSettingsExpanded,
  onAdvancedSettingsToggle,
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  
  const [ localType, setType ] = useState(1);
  const [ placeholder, setPlaceholder ] = useState(undefined);
  const [ hasDifferentSettingsInSections, setHasDifferentSettingsInSections ] = useState(undefined);
  const [ localInterval, setInterval ] = useState(RoundIntervals.FULL);
  const [ localLevel, setLevel ] = useState(BreakdownTableDepths.TASK);
  const [ usedSections, setUsedSections ] = useState([]);
  const [ differentSectionRoundingAlertVisible, showDifferentSectionRoundingAlert ] = useState(false);
  
  const { id } = workType;
  
  const expanded = useMemo(() => (
    expandedAccordion === WORK_TYPE_SETTINGS.ROUNDING
  ), [ expandedAccordion ]);
  
  const disabled = useMemo(() => (
    typeof localType !== "number" || typeof localInterval !== "number" || (localLevel >= 0 && usedSections.length === 0)
  ), [ localType, localInterval, localLevel, usedSections ]);
  
  useEffect(() => {
    if(!expanded)
      return;
    
    resetSettings();
  }, [ expanded ]);
  
  const resetSettings = () => {
    const currentRounding = structure.getGlobalWorkTypeRoundingSettings(id);
    
    setType(currentRounding.roundType);
    setInterval(currentRounding.roundInterval);
    setLevel(currentRounding.roundLevel);
    setPlaceholder(currentRounding.placeholder);
    setUsedSections(currentRounding.sections);
    setHasDifferentSettingsInSections(currentRounding.differentPercents);
  }

  const handleToggle = () => onToggle(WORK_TYPE_SETTINGS.ROUNDING, true);
  const handleClose = () => onToggle(WORK_TYPE_SETTINGS.ROUNDING);

  const handleReset = () => {
    structure.setWorkTypeRounding(workType.id);
    handleClose();
  };

  const checkSaveRoundingSettings = () => {
    if(hasDifferentSettingsInSections)
      showDifferentSectionRoundingAlert(true);
    else
      saveRoundingSettings();
  }

  const saveRoundingSettings = () => {
    handleClose();
    structure.setWorkTypeRounding(workType.id, localType, localInterval, localLevel, usedSections);
    showDifferentSectionRoundingAlert(false);
  };

  return (
    <Accordion
      id={WORK_TYPE_SETTINGS.ROUNDING}
      title={<>
        <Cached color="primary" className="text-lg" />
        <h3 className="ml-2">{t("views.editor.dialogs.work_type_settings.rounding")}</h3>
      </>}
      expanded={expanded}
      onExpand={handleToggle}
      titleColored
      allowOverflow
    >
      <Grid item container direction="column" className="p-4 gap-2">
        <RoundingSettingsContents
          globalSettings
          type={localType}
          level={localLevel}
          interval={localInterval}
          intervalPlaceholder={placeholder}
          usedSections={usedSections}
          availableSections={structure.sections}
          onTypeChange={setType}
          onLevelChange={setLevel}
          onIntervalChange={setInterval}
          onSelectSection={setUsedSections}
          showSectionsSettings={advancedSettingsExpanded}
          onAdvancedSettingsToggle={onAdvancedSettingsToggle}
        />
        <Grid item container justifyContent="flex-end" className="mt-4 gap-2">
          <Button
            variant="outlined"
            className="w-max"
            onClick={handleClose}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="outlined"
            className="w-max"
            onClick={handleReset}
          >
            {t("views.editor.dialogs.roundings.reset")}
          </Button>
          <Button
            variant="contained"
            className="w-max"
            onClick={checkSaveRoundingSettings}
            disabled={disabled}
          >
            {t("common.apply")}
          </Button>
        </Grid>
        <Alert
          title={t("views.editor.dialogs.roundings.different_section_rounding_alert.title")}
          isOpen={differentSectionRoundingAlertVisible}
          cancelText={t("common.cancel_full")}
          acceptText={t("views.editor.dialogs.roundings.different_section_rounding_alert.accept")}
          onCancel={() => showDifferentSectionRoundingAlert(false)}
          onAccept={saveRoundingSettings}
          width={480}
        >
          <p>{t("views.editor.dialogs.roundings.different_section_rounding_alert.content1", { name })}</p>
          <br />
          <p>{t("views.editor.dialogs.roundings.different_section_rounding_alert.content2")}</p>
        </Alert>
      </Grid>
    </Accordion>
  );
});

RoundingSettings.propTypes = {
  workType: object.isRequired,
  onToggle: func.isRequired,
  expandedAccordion: string,
  advancedSettingsExpanded: bool,
  onAdvancedSettingsToggle: func,
};
